/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。
 */
import { BusinessPurpose } from '@ebalance/proto/bfftypes/v1/business_purpose_pb'
import { useTranslation } from 'react-i18next'

import { Text } from '@enechain/ecloud-designsystem'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import type { BusinessMasterRow } from '~/hooks/businessMaster/useListBusinessMasters'
import type { CivilDate } from '~/models/civilDate'
import {
  businessPurposeLabel,
  licenseTypesLabel,
} from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/labels'
import { ExpiredBadge } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredBadge'
import { formatCivilDate } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

// 未入力表示 (AC-OMU-013)。 validTo 列など本テーブルの既存「未設定」表示と
// 揃える (design admin/occto.html は「-」表記だが、 本テーブルは em dash '—'
// を「未設定」の既定記号として使う既存 convention)。
const NO_VALUE = '—'

type Props = {
  rows: BusinessMasterRow[]
  isLoading: boolean
  onEdit: (row: BusinessMasterRow) => void
  onDelete: (row: BusinessMasterRow) => void
  today: CivilDate
  /** 空 state のメッセージ上書き (AC-09)。 */
  emptyLabel?: string
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

export const BusinessMasterTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  today,
  emptyLabel,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columns: Array<MasterDataTableColumn<BusinessMasterRow>> = [
    // [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // design (`design/admin/occto.html` 事業者タブ) は 5 列目 (種別 と 取得目的 の間) だが、
    // 行の状態は他の属性を読む前に分かる必要があるため一覧の左端へ統一した。
    {
      key: 'status',
      header: t('page:adminOcctoMaster.common.column.status'),
      cell: (row) => (
        <ExpiredBadge
          today={today}
          validFrom={row.validFrom}
          validTo={row.validTo}
        />
      ),
    },
    {
      key: 'registrationNumber',
      header: t(
        'page:adminOcctoMaster.businessMaster.column.registrationNumber',
      ),
      cell: (row) => row.registrationNumber ?? NO_VALUE,
    },
    {
      key: 'businessCode',
      header: t('page:adminOcctoMaster.businessMaster.column.businessCode'),
      cell: (row) => row.businessCode,
    },
    {
      key: 'businessName',
      header: t('page:adminOcctoMaster.businessMaster.column.businessName'),
      cell: (row) => row.businessName,
    },
    {
      key: 'licenseTypes',
      header: t('page:adminOcctoMaster.businessMaster.column.licenseTypes'),
      cell: (row) => licenseTypesLabel(row.licenseTypes),
    },
    {
      key: 'purpose',
      header: t('page:adminOcctoMaster.businessMaster.column.purpose'),
      // 取得目的が「通常」の行は design (admin/occto.html L874) に合わせて
      // low-emphasis の「—」表示にする (= 通常が大半のため、非通常だけ目立たせる)。
      cell: (row) =>
        row.purpose === BusinessPurpose.NORMAL ? (
          <Text color="Text.lowEmphasis">—</Text>
        ) : (
          businessPurposeLabel(row.purpose)
        ),
    },
    {
      key: 'validFrom',
      header: t('page:adminOcctoMaster.common.column.validFrom'),
      cell: (row) => formatCivilDate(row.validFrom),
    },
    {
      key: 'validTo',
      header: t('page:adminOcctoMaster.common.column.validTo'),
      cell: (row) =>
        row.validTo !== undefined ? formatCivilDate(row.validTo) : NO_VALUE,
    },
    {
      key: 'updatedAt',
      header: t('page:adminOcctoMaster.businessMaster.column.updatedAt'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      // TODO(rbac): spec 権限テーブル「需給管理者のみ CUD 可」は未実装。
      // 認可方針確立後に標準行アクションを disabled / 非表示にする
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
